import React, { useState, useEffect, useMemo } from 'react';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import { target_package_plan } from '../../../../constants/index';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { useDispatch } from 'react-redux';

interface DowngradeMembershipProps {
  onClose: () => void;
  student_plan_uuid: string;
  loadStudentData: () => void;
  student_current_plan: string;
  is_prepaid: number;
  is_subscribed: number;
  no_of_weeks_originally_enrolled_to: number;
  no_of_cycle_remaining: number;
}

function Downgrademembership({
  onClose,
  student_plan_uuid,
  loadStudentData,
  student_current_plan,
  is_prepaid,
  is_subscribed,
  no_of_weeks_originally_enrolled_to,
  no_of_cycle_remaining
}: DowngradeMembershipProps) {
  const [downgradePackageUUID, setDowngradePackageUUID] = useState('');
  const [noOfWeeks, setNoOfWeeks] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [reasonForDowngrade, setReasonForDowngrade] = useState('');
  const dispatch = useDispatch();
  const set_notification_variable = (show: boolean, type: 'success' | 'error', mes: string) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  };

  const currentIndex = target_package_plan.findIndex((pkg) => pkg.value === student_plan_uuid);

  const downgradeOptions = useMemo(() => {
    if (currentIndex === -1) return [];
    return target_package_plan.slice(0, is_prepaid === 0 ? currentIndex : currentIndex + 1);
  }, [currentIndex]);

  const weeksoptinons = [4, 12, 24, 52];

  // Filter weeks for prepaid subscribed users when choosing the same package:
  // Only show weeks strictly less than originally enrolled weeks.
  const filteredWeeksOptions = useMemo(() => {
    if (is_prepaid === 1 && is_subscribed === 1 && downgradePackageUUID === student_plan_uuid) {
      return weeksoptinons.filter((w) => w < Number(no_of_weeks_originally_enrolled_to || 0));
    }
    return weeksoptinons;
  }, [
    is_prepaid,
    is_subscribed,
    downgradePackageUUID,
    student_plan_uuid,
    no_of_weeks_originally_enrolled_to
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDowngradePackageUUID(e.target?.value);
  };

  useEffect(() => {
    setNoOfWeeks('');
    setErrorMessage('');
  }, [downgradePackageUUID]);

  const handleWeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoOfWeeks(e.target?.value);
  };

  const getPackageName = (uuid) => {
    let packageName = target_package_plan.filter((item) => item.value === uuid);
    return packageName[0].label;
  };

  const handleSubmit = async () => {
    if (reasonForDowngrade.trim() === '') {
      return setErrorMessage('Please provide a reason for the downgrade.');
    }

    // Prepaid subscribed users: prevent downgrade if same package and weeks are same or more
    if (
      is_prepaid === 1 &&
      is_subscribed === 1 &&
      downgradePackageUUID === student_plan_uuid &&
      parseInt(noOfWeeks || '0', 10) >= Number(no_of_weeks_originally_enrolled_to || 0)
    ) {
      const msg = 'Downgrade is not possible for the same package with equal or more weeks.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
      return;
    }

    const id = new URLSearchParams(window.location.search).get('id');
    const apiUrl = get_api_url('membership_downgrade');
    const payload = {
      student_uuid: id,
      downgrade_package_uuid: downgradePackageUUID,
      reason: reasonForDowngrade,
      no_of_weeks: Number(noOfWeeks),
      utm_medium: 'desktop_web',
      app_platform: 'web_app',
      device_type: 'desktop'
    };

    try {
      const response = await post_api(apiUrl, payload, true);
      if (response.data.status === 'success') {
        set_notification_variable(
          true,
          'success',
          `Downgrade Successful to ${getPackageName(downgradePackageUUID)}`
        );
        loadStudentData();
        onClose();
      }
    } catch (error) {
      console.log('error', error.response);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong. Please try again.';
      setErrorMessage(msg);
      set_notification_variable(true, 'error', msg);
    }
  };

  return (
    <div className="w-[430px]">
      <hr className="mb-5 border-t border-gray-300" />

      <div className="mb-3">
        <b>Current Plan:</b> <span>{student_current_plan}</span>
        <span>{is_prepaid === 0 ? `/Monthly` : null}</span>
      </div>

      {is_prepaid === 1 && (
        <>
          <div className="mb-3">
            <b>No. of weeks enrolled to:</b> <span>{no_of_weeks_originally_enrolled_to}</span>
          </div>

          <div className="mb-3">
            <b>No. of billing cycles left:</b> <span>{no_of_cycle_remaining}</span>
          </div>
        </>
      )}

      <CustomSingleSelect
        label="Target package plan"
        options={downgradeOptions}
        onChange={handleChange}
        name="downgrade_package_uuid"
        value={downgradePackageUUID}
        style={{ width: '100%' }}
      />

      <CustomSingleSelect
        label="Number of weeks"
        options={filteredWeeksOptions.map((week) => ({
          label: week.toString(),
          value: week.toString()
        }))}
        onChange={handleWeekChange}
        name="no_of_weeks"
        value={noOfWeeks}
        style={{ width: '100%' }}
      />

      <div className="mb-3">
        <label
          className="block mt-6 mb-1 text-sm font-semibold text-gray-700"
          style={{ textTransform: 'unset' }}
        >
          Reason For Downgrade <span className="text-[red]">*</span>
        </label>
        <textarea
          className="h-[80px] border border-gray-300 rounded-md w-full p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={reasonForDowngrade}
          onChange={(e) => setReasonForDowngrade(e.target.value)}
          placeholder="Enter the reason for the Downgrade..."
        />
      </div>

      {errorMessage && (
        <div className="p-3 mt-5 mb-5 text-red-800 bg-red-100 rounded">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      <div className="flex justify-center gap-4">
        <button
          onClick={onClose}
          className="w-full px-6 py-2 text-black bg-gray-200 border border-gray-400 rounded hover:bg-gray-300"
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={!downgradePackageUUID || !noOfWeeks}
          className={`px-6 py-2 w-full rounded border text-white 
            ${
              !downgradePackageUUID || !noOfWeeks
                ? 'bg-red-600 border-red-700 opacity-60 cursor-not-allowed'
                : 'bg-red-600 border-red-700 hover:bg-red-700 cursor-pointer'
            }`}
        >
          Submit
        </button>
      </div>
    </div>
  );
}

export default Downgrademembership;
